/**
 * Оценка готовности договора к подписанию (HITL flow).
 *
 * Логика:
 * - Считаем «активными» риски, по которым юрист НЕ принял решение
 * «rejected» (LLM ошибся) и НЕ «disputed» (вынесено на отдельное
 * обсуждение). Всё прочее — потенциально пойдёт в протокол разногласий
 * и тревожит компанию.
 * - Если есть хоть один активный critical → красный «не подписывать»
 * - Если есть активные major (но нет critical) → жёлтый «требует переговоров»
 * - Только minor / всё отклонено → зелёный «готов к подписанию»
 * - Анализ ещё идёт → серый «ждём LLM»
 */

import type { ContractRisk } from '@entities/legal/types';

export type SigningStatus = 'green' | 'yellow' | 'red' | 'pending' | 'empty';

export interface SigningVerdict {
  status: SigningStatus;
  label: string;
  reason: string;
  active_critical: number;
  active_major: number;
  active_minor: number;
}

function isActive(r: ContractRisk): boolean {
  return r.decision !== 'rejected' && r.decision !== 'disputed';
}

export function scoreContract(contractStatus: string, risks: ContractRisk[]): SigningVerdict {
  if (contractStatus === 'under_review') {
    return {
      status: 'pending',
      label: 'Анализ ещё идёт',
      reason: 'LLM анализирует договор. Оценка готовности появится после завершения.',
      active_critical: 0,
      active_major: 0,
      active_minor: 0,
    };
  }
  if (risks.length === 0) {
    return {
      status: 'empty',
      label: 'Нет данных',
      reason:
        'Анализа ещё не было либо рисков не найдено. Загрузите файл договора, чтобы получить экспертизу.',
      active_critical: 0,
      active_major: 0,
      active_minor: 0,
    };
  }

  const active = risks.filter(isActive);
  const active_critical = active.filter((r) => r.severity === 'critical').length;
  const active_major = active.filter((r) => r.severity === 'major').length;
  const active_minor = active.filter((r) => r.severity === 'minor').length;

  if (active_critical > 0) {
    return {
      status: 'red',
      label: 'Не подписывать',
      reason:
        `Найдено критических рисков: ${active_critical}. Без переговоров и корректировки условий — заключение договора с такими ` +
        `формулировками может повлечь серьёзные убытки для АО «САТКОМ».`,
      active_critical,
      active_major,
      active_minor,
    };
  }
  if (active_major > 0) {
    return {
      status: 'yellow',
      label: 'Требует переговоров',
      reason:
        `Критических рисков нет, но есть важные: ${active_major}. Подпишите только после ` +
        `согласования протокола разногласий — иначе условия будут невыгодны компании.`,
      active_critical,
      active_major,
      active_minor,
    };
  }
  return {
    status: 'green',
    label: 'Готов к подписанию',
    reason:
      active_minor > 0
        ? `Все критические и важные риски сняты, осталось ${active_minor} незначительных замечаний — их можно проигнорировать или включить в протокол по желанию.`
        : 'Все риски проработаны юристом. Договор можно подписывать в текущей редакции.',
    active_critical,
    active_major,
    active_minor,
  };
}
